import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'
import styles from './Pagination.module.scss'

export interface PaginationProps {
  /** 1-based current page. */
  currentPage: number
  totalPages: number
  totalItems: number
  perPage: number
  onPageChange: (page: number) => void
  /** Accessible label for screen readers. */
  'aria-label'?: string
  /** Optional: show the "Mostrando X–Y de Z" caption (default true). */
  showRange?: boolean
  className?: string
}

/**
 * Pagination control matching the Prism `ranking.html` design:
 * "Mostrando 1–24 de 106 · 24 por página" on the left, numeric buttons +
 * chevrons on the right. Numbers elide with `…` when total pages is large.
 *
 * Keyed changes live in the URL — the consumer is expected to pass
 * `currentPage` from search params and write back through `onPageChange`.
 */
export function Pagination({
  currentPage,
  totalPages,
  totalItems,
  perPage,
  onPageChange,
  'aria-label': ariaLabel = 'Paginação',
  showRange = true,
  className,
}: PaginationProps) {
  if (totalPages <= 1) return null

  const start = (currentPage - 1) * perPage + 1
  const end = Math.min(currentPage * perPage, totalItems)
  const tokens = buildTokens(currentPage, totalPages)

  return (
    <nav aria-label={ariaLabel} className={cn(styles.wrap, className)}>
      {showRange ? (
        <span className={styles.caption}>
          Mostrando {formatNumber(start)}–{formatNumber(end)} de {formatNumber(totalItems)} ·{' '}
          <b className={styles.captionEmphasis}>
            {formatNumber(perPage)} por página
          </b>
        </span>
      ) : (
        <span />
      )}
      <div className={styles.buttons}>
        <button
          type="button"
          className={styles.btn}
          disabled={currentPage <= 1}
          onClick={() => {
            onPageChange(currentPage - 1)
          }}
          aria-label="Página anterior"
        >
          <ChevronLeft aria-hidden />
        </button>
        {tokens.map((t, i) =>
          t === 'ellipsis' ? (
            <span key={`e-${i.toString()}`} className={styles.ellipsis} aria-hidden>
              …
            </span>
          ) : (
            <button
              key={t}
              type="button"
              className={cn(styles.btn, t === currentPage && styles.active)}
              aria-current={t === currentPage ? 'page' : undefined}
              onClick={() => {
                onPageChange(t)
              }}
            >
              {t}
            </button>
          ),
        )}
        <button
          type="button"
          className={styles.btn}
          disabled={currentPage >= totalPages}
          onClick={() => {
            onPageChange(currentPage + 1)
          }}
          aria-label="Próxima página"
        >
          <ChevronRight aria-hidden />
        </button>
      </div>
    </nav>
  )
}

type Token = number | 'ellipsis'

/**
 * Build the elision pattern used across the design system. Always shows
 * page 1 and last; shows current ±1; inserts an ellipsis when the gap is
 * > 1. De-duplicated, so small page counts don't double-render the edges.
 */
function buildTokens(current: number, total: number): Token[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  const raw = new Set<number>([1, total, current - 1, current, current + 1])
  const pages = [...raw].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b)

  const tokens: Token[] = []
  let prev: number | null = null
  for (const p of pages) {
    if (prev !== null && p - prev > 1) tokens.push('ellipsis')
    tokens.push(p)
    prev = p
  }
  return tokens
}
